/**
 * GA4 analytics module -- the single place events are sent from.
 *
 * SAFETY: test traffic must never reach the LIVE GA4 property / GTM container.
 * The live IDs are only ever used when BOTH hold:
 *   1. the build is a production build (REACT_APP_ENV=production), and
 *   2. window.location.hostname is one of PRODUCTION_HOSTS.
 * Everywhere else (localhost, staging, uat, preview hosts, a production build served from an
 * unknown host) the live IDs are never loaded, even if the env vars are set.
 *
 * Modes (see getMode):
 *   'live'    production build on a production host: gtag loads with config.GA4_ID.
 *   'test'    REACT_APP_GA4_DEBUG=true AND REACT_APP_GA4_TEST_ID set to a non-live ID:
 *             gtag loads with the test ID, debug_mode:true, no GTM.
 *   'dry-run' default everywhere else: nothing is sent, events are only console.info'd.
 */
import { config, isProduction } from './environment';

/** The live GA4 measurement ID and GTM container. Kept here only so they can be REFUSED
 * outside production hosts; the live GA4 ID itself still comes from REACT_APP_GA4_ID. */
export const LIVE_GA4_ID = 'G-78SGWPRR9P';
export const LIVE_GTM_ID = 'GTM-NXQ69FX';

/** Hostnames (exact match) where the React app is served in production. The single source of
 * truth for the guard. app.athosinsurance.com is the React origin named in
 * ShortTermApplication/Index.jsx; the apex / www are included because production
 * ATHOS_DOT_NET_URL is https://athosinsurance.com. staging.athosinsurance.com and
 * localhost are deliberately NOT here. */
// The React app's production host (legacy Web.config ReactBaseUrl). athosinsurance.com / www are the .NET site.
export const PRODUCTION_HOSTS = ['app.athosinsurance.com'];

export const MODES = Object.freeze({ live: 'live', test: 'test', dryRun: 'dry-run' });

const SESSION_KEY_PREFIX = 'ga4_sent_';

let currentUserId = null;
/** Plan section 4 "common parameters": attached to every event until changed (e.g. the current
 * application's internal id and where the user came from). Event params win on a clash. */
let commonParams = {};
let warnedLiveTestId = false;
let warnedUnknownHost = false;

export const isProductionHost = () => {
    try { return PRODUCTION_HOSTS.includes(window.location.hostname); } catch (e) { return false; }
};

const getTestId = () => (process.env.REACT_APP_GA4_TEST_ID || '').trim();
const isDebugFlag = () => process.env.REACT_APP_GA4_DEBUG === 'true';

/** Resolve the current mode. Evaluated on every call (cheap) so it always reflects the real
 * host / env. */
export function getMode() {
    const prodBuild = isProduction();
    if (prodBuild && isProductionHost() && config.GA4_ID) return MODES.live;

    if (prodBuild && !isProductionHost() && !warnedUnknownHost) {
        warnedUnknownHost = true;
        console.warn(`[GA4] Production build on non-production host "${window.location.hostname}": live GA4/GTM disabled. `
            + 'If this IS the production site, add its hostname to PRODUCTION_HOSTS in Utils/analytics.js.');
    }

    const testId = getTestId();
    if (isDebugFlag() && testId) {
        if (testId === LIVE_GA4_ID || testId === config.GA4_ID) {
            if (!warnedLiveTestId) {
                warnedLiveTestId = true;
                console.warn('[GA4] REACT_APP_GA4_TEST_ID equals the LIVE property ID; refusing to use it. Running in dry-run.');
            }
            return MODES.dryRun;
        }
        return MODES.test;
    }
    return MODES.dryRun;
}

/** Measurement ID for the current mode, or null in dry-run. */
export function getMeasurementId() {
    const mode = getMode();
    if (mode === MODES.live) return config.GA4_ID;
    if (mode === MODES.test) return getTestId();
    return null;
}

/** True only in 'live' mode: the one case GTM (and the other ad pixels) may load. */
export const canLoadLiveTags = () => getMode() === MODES.live;

//#region Sanitizer
const BLOCKED_KEYS = new Set([
    'name', 'firstname', 'lastname', 'fullname', 'username', 'policyholdername', 'applicantname',
    'contactname', 'companyname', 'businessname', 'cardholdername',
    'address', 'address1', 'address2', 'streetaddress', 'street', 'mailingaddress',
    'mobile', 'telephone', 'tel', 'fax', 'ssn', 'dob', 'dateofbirth',
]);
const EMAIL_VALUE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const PHONE_VALUE = /^\+?[\d\s().-]+$/;

const isBlockedKey = (key) => {
    const k = String(key).toLowerCase().replace(/[^a-z0-9]/g, '');
    return BLOCKED_KEYS.has(k) || k.includes('email') || k.includes('phone');
};
const looksLikePII = (value) => {
    if (typeof value !== 'string') return false;
    if (EMAIL_VALUE.test(value)) return true;
    const digits = value.replace(/\D/g, '');
    return PHONE_VALUE.test(value.trim()) && digits.length >= 10;
};

/** Returns a copy of `params` without personal data: keys such as name / email / phone /
 * address, and string values that look like an email address or phone number. Recurses into
 * objects and arrays (e.g. `items`). */
export function sanitizeParams(params) {
    if (Array.isArray(params)) {
        return params.filter((v) => !looksLikePII(v)).map((v) => sanitizeParams(v));
    }
    if (params && typeof params === 'object') {
        const out = {};
        Object.keys(params).forEach((key) => {
            if (isBlockedKey(key)) return;
            const value = params[key];
            if (looksLikePII(value)) return;
            out[key] = sanitizeParams(value);
        });
        return out;
    }
    return params;
}
//#endregion

//#region gtag loading
function ensureGtagStub() {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
        window.gtag = function gtag() { window.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
    }
}

/** Load gtag.js for the current mode. Does nothing in dry-run (so the live tag can never load
 * on localhost / staging). Safe to call more than once. */
export function initAnalytics() {
    const mode = getMode();
    const id = getMeasurementId();
    if (mode === MODES.dryRun || !id) return;

    ensureGtagStub();
    if (!document.getElementById('athos-ga4-script')) {
        const script = document.createElement('script');
        script.id = 'athos-ga4-script';
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
        document.head.appendChild(script);
    }
    if (!window.__athosGa4Configured) {
        window.__athosGa4Configured = true;
        window.gtag('js', new Date());
        const cfg = { send_page_view: false };
        if (mode === MODES.test) cfg.debug_mode = true;
        if (currentUserId) cfg.user_id = currentUserId;
        window.gtag('config', id, cfg);
    }
}
//#endregion

const send = (event, params) => {
    const mode = getMode();
    const clean = sanitizeParams({ ...commonParams, ...(params || {}) });
    if (mode === MODES.dryRun) {
        console.info(`[GA4] ${event}`, clean);
        return;
    }
    initAnalytics();
    if (typeof window.gtag !== 'function') return;
    if (mode === MODES.test) {
        clean.debug_mode = true;
        console.info(`[GA4] ${event}`, clean);
    }
    window.gtag('event', event, clean);
};

/** Merge into the params sent on every event (`application_id`, `source`). A null / empty value
 * removes that key. Internal ids only -- the sanitizer still strips anything that looks like PII. */
export function setCommonParams(params) {
    try {
        const next = { ...commonParams };
        Object.keys(params || {}).forEach((key) => {
            const value = params[key];
            if (value === undefined || value === null || value === '') delete next[key];
            else next[key] = String(value);
        });
        commonParams = next;
    } catch (e) { /* analytics must never break the app */ }
}

/** Send a GA4 event. Personal data is stripped from params. */
export function track(event, params) {
    try { send(event, params); } catch (e) { /* analytics must never break the app */ }
}

/** Send a conversion event at most once per (event, id) per browser session.
 * @returns {boolean} true if it was sent now, false if it was a repeat. */
export function fireOnce(event, id, params) {
    const key = `${SESSION_KEY_PREFIX}${event}_${id}`;
    try { if (window.sessionStorage.getItem(key)) return false; } catch (e) { /* storage blocked: fall through */ }
    track(event, params);
    try { window.sessionStorage.setItem(key, '1'); } catch (e) { /* ignore */ }
    return true;
}

/** Set the GA4 user_id (internal user id only, never an email). */
export function setUserId(id) {
    const value = id === undefined || id === null ? '' : String(id).trim();
    if (!value || value.includes('@') || looksLikePII(value) || value === currentUserId) return;
    currentUserId = value;
    const mode = getMode();
    if (mode === MODES.dryRun) { console.info('[GA4] set user_id', value); return; }
    initAnalytics();
    if (typeof window.gtag === 'function') window.gtag('set', { user_id: value });
}

/** Clear the GA4 user_id (logout). */
export function clearUserId() {
    if (currentUserId === null) return;
    currentUserId = null;
    const mode = getMode();
    if (mode === MODES.dryRun) { console.info('[GA4] clear user_id'); return; }
    if (typeof window.gtag === 'function') window.gtag('set', { user_id: null });
}

/** Send a page_view. page_location is origin + pathname only (query string and hash are
 * dropped so personal data in URLs is never sent). `location` may be a path string or a
 * router location object. */
export function pageView(location, title) {
    const pathname = typeof location === 'string' ? location.split(/[?#]/)[0] : (location && location.pathname) || window.location.pathname;
    track('page_view', {
        page_location: `${window.location.origin}${pathname}`,
        page_title: title,
    });
}
